import { IActionPlanSummary } from '../../../../../services/api/anomaly/AnomalyService';

export type ActionPlanOrderBy = 'data_limite_seguranca' | 'status' | 'origem' | 'acao';
export type ActionPlanOrderDirection = 'asc' | 'desc';

export type ActionPlanStatus = NonNullable<IActionPlanSummary['status']>;

const STATUS_PRIORITY: Record<ActionPlanStatus, number> = {
    'NAO INICIADO': 0,
    'EM ANDAMENTO': 1,
    FINALIZADO: 2,
};

// Fonte única de label/cor do status, usada tanto pelo filtro quanto pela exibição na tabela.
export const ACTION_PLAN_STATUS_META: Record<ActionPlanStatus, { label: string; color: 'default' | 'warning' | 'success' }> = {
    'NAO INICIADO': { label: 'Não Iniciado', color: 'default' },
    'EM ANDAMENTO': { label: 'Em Andamento', color: 'warning' },
    FINALIZADO: { label: 'Finalizado', color: 'success' },
};

export const ALL_ACTION_PLAN_STATUSES: ActionPlanStatus[] = Object.keys(ACTION_PLAN_STATUS_META) as ActionPlanStatus[];

// Filtra os planos pelos status visíveis. Planos sem status definido são tratados
// como 'NAO INICIADO' (mesmo default usado na exibição do Chip em ActionPlanPage.tsx).
export function filterActionPlans(
    plans: IActionPlanSummary[],
    visibleStatuses: ActionPlanStatus[],
) {
    return plans.filter(plan => visibleStatuses.includes(plan.status ?? 'NAO INICIADO'));
}

function compareNullableValues(
    valueA: number | string | null,
    valueB: number | string | null,
    direction: ActionPlanOrderDirection,
) {
    if (valueA === null && valueB === null) return 0;
    if (valueA === null) return 1;
    if (valueB === null) return -1;

    if (valueA < valueB) {
        return direction === 'asc' ? -1 : 1;
    }

    if (valueA > valueB) {
        return direction === 'asc' ? 1 : -1;
    }

    return 0;
}

function getDateTimestamp(dateValue?: string) {
    if (!dateValue) return null;

    const normalizedDate = dateValue.includes(' ') && !dateValue.includes('T')
        ? dateValue.replace(' ', 'T')
        : dateValue;

    const timestamp = new Date(normalizedDate).getTime();

    if (Number.isNaN(timestamp)) {
        return null;
    }

    return timestamp;
}

function getStatusPriority(status?: IActionPlanSummary['status']) {
    if (!status) return null;

    return STATUS_PRIORITY[status] ?? null;
}

function normalizeTextValue(text?: string | null) {
    if (!text) return null;

    const normalizedValue = text
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim()
        .toLowerCase();

    return normalizedValue === '' ? null : normalizedValue;
}

export function sortActionPlans(
    plans: IActionPlanSummary[],
    orderBy: ActionPlanOrderBy,
    orderDirection: ActionPlanOrderDirection,
) {
    return [...plans].sort((planA, planB) => {
        switch (orderBy) {
            case 'data_limite_seguranca':
                return compareNullableValues(
                    getDateTimestamp(planA.data_limite_seguranca),
                    getDateTimestamp(planB.data_limite_seguranca),
                    orderDirection,
                );
            case 'status':
                return compareNullableValues(
                    getStatusPriority(planA.status),
                    getStatusPriority(planB.status),
                    orderDirection,
                );
            case 'origem':
                return compareNullableValues(
                    normalizeTextValue(planA.origem),
                    normalizeTextValue(planB.origem),
                    orderDirection,
                );
            case 'acao':
                return compareNullableValues(
                    normalizeTextValue(planA.acao),
                    normalizeTextValue(planB.acao),
                    orderDirection,
                );
            default:
                return 0;
        }
    });
}
